import {Event} from '@/apiTypes/event';
import type {EventListSummary} from '@/apiTypes/eventList';
import {AddToListButton} from '@/components/addToListButton/addToListButton';
import {EventTypeBadge} from '@/components/eventTypeBadge/eventTypeBadge';
import {GoverningBodyIcon} from '@/components/governingBodyIcon/governingBodyIcon';
import {ShareButton} from '@/components/shareButton/shareButton';
import {palette} from '@/styles/colors';
import {formatPriceRange, getPriceRange} from '@/utils/currencyUtils';
import {formatEventDate, formatTime} from '@/utils/displayFormatUtils';
import {formatLocationShort} from '@/utils/formatLocationShort/formatLocationShort';
import {Link} from '@tanstack/react-router';
import {ChevronLeft} from 'lucide-react';
import {type Variants, motion} from 'motion/react';
import {FC} from 'react';
import {Button} from '../ui/button';
import {Pill} from '../ui/pill';
import {Typography} from '../ui/typography';

interface EventHeroProps {
  event: Event;
  lists: EventListSummary[];
  memberListIds: string[];
  children?: React.ReactNode;
}

const heroContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const heroItemVariants: Variants = {
  hidden: {opacity: 0, y: 30, filter: 'blur(4px)'},
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 20,
    },
  },
};

/** Full-width hero banner with event image, badges, and title. */
export const EventHero: FC<EventHeroProps> = ({
  event,
  lists,
  memberListIds,
  children,
}) => {
  const priceRange = getPriceRange(event);
  const firstCategoryStartTime = event.categories?.[0]?.startTime;
  return (
    <div className="relative">
      {/* Hero Image */}
      <div
        className="-mt-[88px] pt-[88px] min-h-56 md:min-h-100 bg-cover bg-center relative flex flex-col"
        style={{
          backgroundImage: event.heroUrl
            ? `url('${event.heroUrl}')`
            : 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent" />
        <div className="relative flex flex-col flex-1">
          {/* Admin bar overlay */}
          {children}
          {/* Top action bar */}
          <div className="p-4 md:p-6">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <Button
                asChild
                variant="overlay"
                className="bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 hover:text-white"
              >
                <Link to="/search">
                  <ChevronLeft className="w-5 h-5" />
                  BACK TO EVENTS
                </Link>
              </Button>
              <div className="flex items-center gap-2">
                <div className="hidden lg:block">
                  <ShareButton
                    title={event.name}
                    contentType="event"
                    contentId={event.id}
                    variant="overlay"
                  />
                </div>
                <AddToListButton
                  eventId={event.id}
                  lists={lists}
                  memberListIds={memberListIds}
                />
              </div>
            </div>
          </div>

          {/* Content overlay */}
          <div className=" flex flex-1 items-end justify-center">
            <motion.div
              className="max-w-5xl flex-1 p-4 md:p-6"
              variants={heroContainerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Tags */}
              <motion.div
                variants={heroItemVariants}
                className="flex items-center gap-2 mb-3"
              >
                {event.governingBody && (
                  <GoverningBodyIcon governingBody={event.governingBody} />
                )}
                <EventTypeBadge
                  eventType={event.eventType}
                  size="sm"
                  variant="overlay"
                />
                {event.discipline && event.discipline !== 'other' && (
                  <Pill
                    size="sm"
                    color={palette.neutral[600]}
                    variant="overlay"
                  >
                    {event.discipline}
                  </Pill>
                )}
                <Typography level="B4" className="text-neutral-100">
                  {event.startDate ? (
                    <time dateTime={event.startDate.toISOString()}>
                      {formatEventDate(event.startDate, event.endDate)}
                    </time>
                  ) : (
                    formatEventDate(event.startDate, event.endDate)
                  )}
                </Typography>
              </motion.div>
              <div className="flex flex-col gap-5">
                <motion.div variants={heroItemVariants}>
                  <Typography
                    level="D1"
                    className="text-white italic text-4xl md:text-5xl"
                  >
                    {event.name}
                  </Typography>
                </motion.div>
                <motion.div variants={heroItemVariants} className="flex gap-4">
                  {event.location && (
                    <EventInfoData
                      label="Location"
                      data={formatLocationShort(event.location)}
                    />
                  )}
                  {firstCategoryStartTime && (
                    <EventInfoData
                      label="Start Time"
                      data={formatTime(firstCategoryStartTime)}
                    />
                  )}
                  {priceRange && priceRange.max !== 0 && (
                    <EventInfoData
                      label="Price"
                      data={formatPriceRange(priceRange)}
                    />
                  )}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EventInfoDataProps {
  label: string;
  data?: string;
}
const EventInfoData: FC<EventInfoDataProps> = ({label, data}) => (
  <div className="flex flex-col">
    <Typography level="C1" className="text-neutral-400">
      {label}
    </Typography>
    {data && (
      <Typography level="B1" className="text-white">
        {data}
      </Typography>
    )}
  </div>
);
